import { useState } from "react";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { T } from "@/lib/testIds";
import { PHONE, EMAIL } from "@/components/Layout";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in the required fields.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/contact", form);
      toast.success("Thank you — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-up">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-[#1D4ED8] font-semibold mb-3">Contact</div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#0A192F]">Let's talk</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">Reach out with any questions about our services or how we can help.</p>

          <div className="mt-8 space-y-4">
            <a href={`tel:${PHONE.replace(/\D/g, "")}`} className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-slate-200 hover:border-[#1D4ED8] transition-colors">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Phone className="w-5 h-5" /></div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Phone</div>
                <div className="font-semibold text-[#0A192F]">{PHONE}</div>
              </div>
            </a>
            <a href={`mailto:${EMAIL}`} className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-slate-200 hover:border-[#1D4ED8] transition-colors">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-[#1D4ED8] flex items-center justify-center"><Mail className="w-5 h-5" /></div>
              <div className="min-w-0">
                <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Email</div>
                <div className="font-semibold text-[#0A192F] break-all text-sm">{EMAIL}</div>
              </div>
            </a>
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-slate-200">
              <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center"><MapPin className="w-5 h-5" /></div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Service Area</div>
                <div className="font-semibold text-[#0A192F]">Florida</div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3 text-sm text-slate-500">
            <span>Mon–Sun · Response within one business day</span>
          </div>
        </div>

        <div className="lg:col-span-3">
          <form onSubmit={submit} data-testid={T.contact.form} className="rounded-3xl bg-white border border-slate-200 p-8 shadow-sm">
            <h2 className="text-2xl font-semibold text-[#0A192F]">Send us a message</h2>
            <p className="mt-1 text-sm text-slate-500">We usually respond within one business day.</p>
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700">Name *</label>
                <input data-testid={T.contact.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none" placeholder="Full name" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Email *</label>
                <input type="email" data-testid={T.contact.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none" placeholder="you@example.com" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-slate-700">Phone</label>
                <input data-testid={T.contact.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none" placeholder="Optional" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-slate-700">Message *</label>
                <textarea data-testid={T.contact.message} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none" placeholder="How can we help?" />
              </div>
            </div>
            <button type="submit" disabled={submitting} data-testid={T.contact.submit} className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1D4ED8] hover:bg-blue-800 disabled:opacity-60 text-white font-semibold transition-colors">
              <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
